import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ConfigProvider } from './providers/ConfigProvider';
import { AuthProvider } from './providers/AuthProvider';
import { UserSessionProvider } from './providers/UserSessionProvider';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { ChunkErrorBoundary } from './components/shared/ChunkErrorBoundary';
import { LoadingFallback } from './components/shared/LoadingFallback';
import { ToastProvider } from './components/shared/ToastContainer';
import { NotificationRenderer } from './components/shared/NotificationRenderer';
import { useAppStore } from './store/appStore';
import { AppShell } from './components/layout/AppShell';
import { CommandPalette } from './components/shared/CommandPalette';
import { ChatPanel } from './components/shared/ChatPanel';
import { KeyboardShortcutsHelp } from './components/shared/KeyboardShortcutsHelp';
import { GuidedTour } from './components/tour/GuidedTour';
import './components/shared/SkipLink.css';
import './components/shared/CommandPalette.css';
import './App.css';

const SearchPage = lazy(() => import('./pages/SearchPage').then(m => ({ default: m.SearchPage })));
const ObservationsPage = lazy(() => import('./pages/ObservationsPage').then(m => ({ default: m.ObservationsPage })));
const AvailabilityPage = lazy(() => import('./pages/AvailabilityPage').then(m => ({ default: m.AvailabilityPage })));
const ObservationsTabPage = lazy(() => import('./pages/ObservationsTabPage').then(m => ({ default: m.ObservationsTabPage })));
const QualityPage = lazy(() => import('./pages/QualityPage').then(m => ({ default: m.QualityPage })));
const NetworkTabbedPage = lazy(() => import('./pages/NetworkTabbedPage').then(m => ({ default: m.NetworkTabbedPage })));
const StationPage = lazy(() => import('./pages/StationPage').then(m => ({ default: m.StationPage })));

// Rank 9: redirect shims for legacy URLs. Keep for ~6 months per Q3 tradeoff.
const RedirectNetworkQuality = () => {
  const { networkId } = useParams<{ networkId: string }>();
  return <Navigate to={`/quality/network/${networkId ?? ''}`} replace />;
};
// Sensor quality moved inline into the station page (per-network QC dropdown).
// Legacy /network/:networkId/quality/:stationCode AND the old
// /quality/network/:networkId/station/:stationCode both land on the station
// page with that network's dropdown auto-opened.
const RedirectSensorQuality = () => {
  const { networkId, stationCode } = useParams<{ networkId: string; stationCode: string }>();
  const q = networkId ? `?network=${encodeURIComponent(networkId)}` : '';
  return <Navigate to={`/station/${encodeURIComponent(stationCode ?? '')}${q}`} replace />;
};
const RedirectStationObservations = () => {
  const { stationCode } = useParams<{ stationCode: string }>();
  return <Navigate to={`/station/${stationCode ?? ''}/observations${window.location.search}`} replace />;
};


// ponytail: 100% of users land on SearchPage — prefetch its chunk on idle so it's
// ready before React even renders the Suspense boundary. Eliminates the lazy-load waterfall.
if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
  requestIdleCallback(() => { import('./pages/SearchPage'); });
} else {
  setTimeout(() => { import('./pages/SearchPage'); }, 100);
}

function App() {
  // Pre-load all station data for autocomplete and map display.
  // Starts fetching immediately so data is ready by the time user navigates to search.
  // ponytail: This is the SINGLE source of station data — StationCacheService reads from
  // this store instead of making its own /api/weather/weatherstations call.
  useEffect(() => {
    const { loadAllStations } = useAppStore.getState();
    loadAllStations();
  }, []);

  return (
    <ErrorBoundary>
      <ToastProvider>
        <ConfigProvider>
          <AuthProvider>
            <BrowserRouter>
              <UserSessionProvider>
                <AppShell>
                  <Routes>
                    <Route path="/" element={<Navigate to="/search/station" replace />} />
                    <Route path="/login" element={<Navigate to="/search/station" replace />} />
                    <Route path="/search/:method" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><SearchPage /></Suspense></ChunkErrorBoundary>} />

                    {/* Observation map (list view) */}
                    <Route path="/observations" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><ObservationsTabPage /></Suspense></ChunkErrorBoundary>} />
                    {/* Rank 9: canonical station-observations route (must appear BEFORE /station/:stationCode to avoid hijack) */}
                    <Route path="/station/:stationCode/observations" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><ObservationsPage /></Suspense></ChunkErrorBoundary>} />
                    {/* Legacy redirect — keep for ~6 months */}
                    <Route path="/observations/:stationCode" element={<RedirectStationObservations />} />

                    {/* Unified Station page — favorites + search + detail */}
                    <Route path="/station" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><StationPage /></Suspense></ChunkErrorBoundary>} />
                    <Route path="/station/:stationCode" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><StationPage /></Suspense></ChunkErrorBoundary>} />
                    <Route path="/stations" element={<Navigate to="/station" replace />} />
                    <Route path="/stations/*" element={<Navigate to="/station" replace />} />

                    <Route path="/availability" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><AvailabilityPage /></Suspense></ChunkErrorBoundary>} />

                    {/* Rank 9 legacy redirects (must appear BEFORE the /network/* wildcard below) */}
                    <Route path="/network/:networkId/quality/:stationCode" element={<RedirectSensorQuality />} />
                    <Route path="/network/:networkId/quality" element={<RedirectNetworkQuality />} />
                    <Route path="/station-quality" element={<Navigate to="/quality" replace />} />
                    <Route path="/statistics" element={<Navigate to="/network/statistics" replace />} />

                    {/* Rank 9: /statistics folds into /network as "By station" tab */}
                    <Route path="/network/*" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><NetworkTabbedPage /></Suspense></ChunkErrorBoundary>} />

                    {/* Rank 9: consolidated /quality page with 3 tabs */}
                    <Route path="/quality/*" element={<ChunkErrorBoundary><Suspense fallback={<LoadingFallback />}><QualityPage /></Suspense></ChunkErrorBoundary>} />

                    <Route path="*" element={<Navigate to="/search/station" replace />} />
                  </Routes>
                </AppShell>
                <CommandPalette />
                <ChatPanel />
                <KeyboardShortcutsHelp />
                <GuidedTour />
              </UserSessionProvider>
            </BrowserRouter>
          </AuthProvider>
        </ConfigProvider>
      </ToastProvider>
      <NotificationRenderer />
    </ErrorBoundary>
  );
}

export default App;
